import { countryName } from "@lib/countryCodes";
import {
  CLEAR_XRAY_SEARCH_RESULTS,
  CLEAR_XRAY_SEARCH_RUN,
  DISMISS_DISCOVERED_PROFILE,
  IMPORT_LINKEDIN_TO_CAMPAIGN,
  RUN_ALL_XRAY_SEARCHES,
  RUN_XRAY_SEARCH,
  UPDATE_DISCOVERED_PROFILE_LOCATION,
} from "@lib/graphql/mutations";
import { MARKET_MAP_QUERY } from "@lib/graphql/queries";
import { runMutation } from "@lib/graphql/unwrapMutation";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Globe,
  Loader2,
  MapPin,
  Play,
  Trash2,
  UserSearch,
  X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import { useCampaignDetail } from "./context";

interface DiscoveredProfileData {
  id: string;
  linkedinUrl: string;
  title: string | null;
  snippet: string | null;
  candidateId: string | null;
  imported: boolean;
  createdAt: string;
  locationMatch: string | null;
  locationMatchSource: string | null;
  inferredLocation: string | null;
  matchedCandidateId: string | null;
  matchType: string | null;
  matchedCandidateRef: string | null;
}

interface XraySearchRun {
  id: string;
  searchQueryId: string;
  queryString: string;
  status: string;
  resultCount: number;
  newCount: number;
  rediscoveredCount: number;
  dismissedCount: number;
  errorMessage: string | null;
  createdAt: string;
  discoveredProfiles: DiscoveredProfileData[];
}

interface SearchQuery {
  id: string;
  query_string: string;
  query_type: string;
  description: string | null;
  linkedin_audience: string | null;
  linkedin_field: string | null;
  created_by: string;
  created_at: string;
}

const XRAY_PREFIX = "site:linkedin.com/in/";

export function DiscoveryTab() {
  const { campaign } = useCampaignDetail();
  const campaignId = campaign.id;
  const [importingUrls, setImportingUrls] = useState<Set<string>>(new Set());
  const [expandedQueries, setExpandedQueries] = useState<Set<string>>(new Set());
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  // Maps a queryId awaiting auto-expand to the id of the latest run that
  // existed when its search was triggered (or null if there was none). We only
  // auto-expand once a run NEWER than that has completed — see the consume
  // block below.
  const pendingExpand = useRef<Map<string, string | null>>(new Map());

  const [{ data, fetching }, reexecute] = useQuery({
    query: MARKET_MAP_QUERY,
    variables: { id: campaignId },
  });
  const [, runXraySearch] = useMutation(RUN_XRAY_SEARCH);
  const [{ fetching: runningAllSearches }, runAllXraySearches] = useMutation(RUN_ALL_XRAY_SEARCHES);
  const [, importLinkedinToCampaign] = useMutation(IMPORT_LINKEDIN_TO_CAMPAIGN);
  const [{ fetching: clearingResults }, clearXraySearchResults] =
    useMutation(CLEAR_XRAY_SEARCH_RESULTS);
  const [, clearXraySearchRun] = useMutation(CLEAR_XRAY_SEARCH_RUN);
  const [, dismissDiscoveredProfile] = useMutation(DISMISS_DISCOVERED_PROFILE);
  const [, updateProfileLocation] = useMutation(UPDATE_DISCOVERED_PROFILE_LOCATION);

  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const resolvedSearchCountryCodes: string[] = data?.campaign?.resolvedSearchCountryCodes ?? [];
  const resolvedSearchLocations: { name: string; countryCode: string }[] =
    data?.campaign?.resolvedSearchLocations ?? [];
  // Narrow locations (city / region) lead when the campaign has them;
  // otherwise fall back to the country list.
  const searchingFrom =
    resolvedSearchLocations.length > 0
      ? resolvedSearchLocations.map((l) => `${l.name} (${countryName(l.countryCode)})`).join(", ")
      : resolvedSearchCountryCodes.map((c) => countryName(c)).join(", ");
  const marketMap = data?.campaign?.marketMap;
  const searchQueries: SearchQuery[] = marketMap?.searchQueries ?? [];
  const xraySearchRuns: XraySearchRun[] = marketMap?.xraySearchRuns ?? [];

  const xrayQueries = searchQueries.filter(
    (q) => q.linkedin_audience === "xray" || q.query_string?.toLowerCase().startsWith(XRAY_PREFIX),
  );

  const latestRunByQuery = new Map<string, XraySearchRun>();
  for (const run of xraySearchRuns) {
    const existing = latestRunByQuery.get(run.searchQueryId);
    if (!existing || new Date(run.createdAt) > new Date(existing.createdAt)) {
      latestRunByQuery.set(run.searchQueryId, run);
    }
  }

  const hasAnyRuns = xraySearchRuns.length > 0;
  const hasActiveRuns = xraySearchRuns.some((r) => r.status === "queued" || r.status === "running");

  const prevRunsRef = useRef(xraySearchRuns);
  if (prevRunsRef.current !== xraySearchRuns) {
    prevRunsRef.current = xraySearchRuns;
    if (pendingExpand.current.size > 0) {
      const toExpand: string[] = [];
      for (const [queryId, priorRunId] of pendingExpand.current) {
        const run = latestRunByQuery.get(queryId);
        if (!run) continue;
        if (run.status === "queued" || run.status === "running") continue;
        // A stale channel refresh can deliver the pre-run result before the
        // freshly-triggered run appears. Only consume the pending entry once a
        // run NEWER than the one present at trigger time has completed;
        // otherwise the old run would auto-expand and swallow the entry, so the
        // real completion never expands.
        if (run.id === priorRunId) continue;
        pendingExpand.current.delete(queryId);
        if (run.discoveredProfiles.length > 0) {
          toExpand.push(queryId);
        }
      }
      if (toExpand.length > 0) {
        setExpandedQueries((prev) => {
          const next = new Set(prev);
          for (const id of toExpand) next.add(id);
          return next;
        });
      }
    }
  }

  const toggleExpanded = (queryId: string) => {
    setExpandedQueries((prev) => {
      const next = new Set(prev);
      if (next.has(queryId)) {
        next.delete(queryId);
      } else {
        next.add(queryId);
      }
      return next;
    });
  };

  const handleRunXraySearch = async (searchQueryId: string) => {
    pendingExpand.current.set(searchQueryId, latestRunByQuery.get(searchQueryId)?.id ?? null);
    await runMutation("running this X-Ray search", () =>
      runXraySearch({ input: { campaignId, searchQueryId } }),
    );
    refresh();
  };

  const handleRunAllXraySearches = async () => {
    for (const q of xrayQueries)
      pendingExpand.current.set(q.id, latestRunByQuery.get(q.id)?.id ?? null);
    await runMutation("running all X-Ray searches", () =>
      runAllXraySearches({ input: { campaignId } }),
    );
    refresh();
  };

  const handleClearResults = async () => {
    if (
      !window.confirm(
        "Clear all X-Ray search results and discovered profiles? This cannot be undone.",
      )
    )
      return;
    await runMutation("clearing the search results", () =>
      clearXraySearchResults({ input: { campaignId } }),
    );
    refresh();
  };

  const handleClearSearchRun = async (runId: string) => {
    if (!window.confirm("Clear this search run and its discovered profiles?")) return;
    await runMutation("clearing this search run", () =>
      clearXraySearchRun({ input: { id: runId } }),
    );
    refresh();
  };

  const handleDismissProfile = async (profileId: string) => {
    await runMutation("dismissing this profile", () =>
      dismissDiscoveredProfile({ input: { id: profileId } }),
    );
    refresh();
  };

  const handleUpdateLocation = async (profileId: string, locationMatch: string) => {
    await runMutation("updating the location", () =>
      updateProfileLocation({ input: { id: profileId, locationMatch } }),
    );
    refresh();
  };

  const handleImportProfile = async (linkedinUrl: string) => {
    setImportingUrls((prev) => new Set(prev).add(linkedinUrl));
    const result = await runMutation("importing this profile", () =>
      importLinkedinToCampaign({ input: { campaignId, linkedinUrl, stage: "pool" } }),
    );
    if (!result) {
      // Import failed (error already surfaced by runMutation). Drop the spinner
      // entry so the row returns to an importable state and the user can retry
      // without a full reload. On success the refetch flips the profile to
      // `imported`, which takes precedence over this set.
      setImportingUrls((prev) => {
        const next = new Set(prev);
        next.delete(linkedinUrl);
        return next;
      });
      return;
    }
    refresh();
  };

  if (fetching && marketMap === undefined) {
    return <p className="text-sm text-text-muted">Loading discovery data...</p>;
  }

  if (xrayQueries.length === 0) {
    return (
      <div className="rounded-lg bg-card p-8 text-center shadow-sm ring-1 ring-ring">
        <UserSearch size={32} className="mx-auto mb-3 text-text-faint" />
        <h3 className="mb-1 text-sm font-medium text-text-secondary">No X-Ray searches yet</h3>
        <p className="text-sm text-text-muted">
          Add X-Ray search queries in the{" "}
          <Link
            to={`/campaigns/${campaignId}/define/market-map`}
            className="text-accent hover:underline"
          >
            Market Map
          </Link>{" "}
          to start discovering candidates.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserSearch size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">X-Ray Searches</h3>
            <span className="text-xs text-text-faint">
              {xrayQueries.length} quer{xrayQueries.length !== 1 ? "ies" : "y"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasAnyRuns && !hasActiveRuns && (
              <button
                type="button"
                onClick={handleClearResults}
                disabled={clearingResults}
                className="flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs font-medium text-text-muted hover:border-red-300 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2 size={12} /> Clear results
              </button>
            )}
            <button
              type="button"
              onClick={handleRunAllXraySearches}
              disabled={runningAllSearches || hasActiveRuns}
              className="flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {runningAllSearches || hasActiveRuns ? (
                <>
                  <Loader2 size={12} className="animate-spin" /> Searching…
                </>
              ) : (
                <>
                  <Play size={12} /> Run all X-Ray searches
                </>
              )}
            </button>
          </div>
        </div>

        {searchingFrom.length > 0 && (
          <div className="mb-2 flex items-center gap-1.5 text-xs text-text-muted">
            <Globe size={12} className="text-text-faint" />
            <span>Searching from: {searchingFrom}</span>
          </div>
        )}

        {hasAnyRuns && (
          <LocationFilterBar filter={locationFilter} onFilterChange={setLocationFilter} />
        )}

        <div className="space-y-1">
          {xrayQueries.map((q) => {
            const latestRun = latestRunByQuery.get(q.id);
            const allProfiles = latestRun?.discoveredProfiles ?? [];
            const profiles = locationFilter
              ? allProfiles.filter((p) => p.locationMatch === locationFilter)
              : allProfiles;
            const hasProfiles = profiles.length > 0;
            const isExpanded = expandedQueries.has(q.id);

            const queryRow = (
              <>
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-text-primary">
                  {q.query_string}
                </span>
                <QueryRunStatus
                  run={latestRun}
                  matchCount={allProfiles.filter((p) => p.locationMatch === "match").length}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRunXraySearch(q.id);
                  }}
                  disabled={latestRun?.status === "queued" || latestRun?.status === "running"}
                  className="flex-shrink-0 rounded p-1 text-text-faint hover:bg-accent/5 hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
                  title="Run this search"
                >
                  {latestRun?.status === "queued" || latestRun?.status === "running" ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Play size={12} />
                  )}
                </button>
              </>
            );

            return (
              <div key={q.id}>
                {hasProfiles ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded border border-border px-3 py-1.5 text-left cursor-pointer hover:bg-accent/5"
                    onClick={() => toggleExpanded(q.id)}
                  >
                    {isExpanded ? (
                      <ChevronDown size={12} className="flex-shrink-0 text-text-faint" />
                    ) : (
                      <ChevronRight size={12} className="flex-shrink-0 text-text-faint" />
                    )}
                    {queryRow}
                  </button>
                ) : (
                  <div className="flex items-center gap-2 rounded border border-border px-3 py-1.5">
                    <span className="w-3 flex-shrink-0" />
                    {queryRow}
                  </div>
                )}

                {isExpanded && hasProfiles && latestRun && (
                  <div className="ml-5 mt-1 space-y-1 border-l-2 border-border pl-3">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleClearSearchRun(latestRun.id)}
                        className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:text-red-600"
                      >
                        <Trash2 size={10} /> Clear
                      </button>
                    </div>
                    {profiles.map((profile) => (
                      <div
                        key={profile.id}
                        className="flex items-start gap-3 rounded border border-border px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <a
                              href={profile.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate text-sm font-medium text-accent hover:underline"
                            >
                              {profile.title || profile.linkedinUrl}
                              <ExternalLink size={10} className="ml-1 inline align-baseline" />
                            </a>
                            <LocationBadge
                              locationMatch={profile.locationMatch}
                              locationMatchSource={profile.locationMatchSource}
                              profileId={profile.id}
                              onOverride={profile.imported ? undefined : handleUpdateLocation}
                            />
                          </div>
                          {profile.snippet && (
                            <p className="mt-0.5 line-clamp-2 text-xs text-text-muted">
                              {profile.snippet}
                            </p>
                          )}
                          {profile.inferredLocation && (
                            <p className="mt-0.5 flex items-center gap-1 text-[10px] text-text-faint">
                              <MapPin size={9} /> {profile.inferredLocation}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-1">
                          {profile.imported ? (
                            <Link
                              to={`/campaigns/${campaignId}/run/pool`}
                              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                            >
                              <CheckCircle2 size={14} /> In pool
                            </Link>
                          ) : profile.matchedCandidateRef ? (
                            <>
                              <span
                                className="flex items-center gap-1 px-2 py-1 text-xs text-amber-600 dark:text-amber-400"
                                title={`Match type: ${profile.matchType}`}
                              >
                                <AlertCircle size={12} />
                                Already in pool as {profile.matchedCandidateRef}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleImportProfile(profile.linkedinUrl)}
                                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
                              >
                                <Download size={12} /> Import anyway
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDismissProfile(profile.id)}
                                className="rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                                title="Dismiss — will not appear in future searches"
                              >
                                <X size={14} />
                              </button>
                            </>
                          ) : importingUrls.has(profile.linkedinUrl) ? (
                            <span className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted">
                              <Loader2 size={14} className="animate-spin" /> Importing…
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => handleImportProfile(profile.linkedinUrl)}
                                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
                              >
                                <Download size={12} /> Import
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDismissProfile(profile.id)}
                                className="rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                                title="Dismiss — will not appear in future searches"
                              >
                                <X size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function QueryRunStatus({ run, matchCount }: { run?: XraySearchRun; matchCount: number }) {
  if (!run) {
    return <span className="text-[10px] text-text-faint">Not run</span>;
  }

  switch (run.status) {
    case "queued":
      return (
        <span className="flex items-center gap-1 text-[10px] text-text-muted">
          <Loader2 size={10} className="animate-spin" /> Queued
        </span>
      );
    case "running":
      return (
        <span className="flex items-center gap-1 text-[10px] text-accent">
          <Loader2 size={10} className="animate-spin" /> Running
        </span>
      );
    case "succeeded": {
      const newCount = run.newCount;
      const rediscovered = run.rediscoveredCount;
      const dismissed = run.dismissedCount;

      // Genuinely empty search: nothing surfaced at all. Distinct from a
      // re-run that only turned up already-known or dismissed profiles.
      if (newCount === 0 && rediscovered === 0 && dismissed === 0) {
        return <span className="text-[10px] text-text-faint">No results</span>;
      }

      return (
        <span className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400">
          <CheckCircle2 size={10} /> {newCount} new
          {rediscovered > 0 && (
            <span className="text-text-faint">&middot; {rediscovered} already found</span>
          )}
          {dismissed > 0 && <span className="text-text-faint">&middot; {dismissed} dismissed</span>}
          {matchCount > 0 && <span className="text-text-faint">&middot; {matchCount} match</span>}
        </span>
      );
    }
    case "failed":
      return (
        <span
          className="flex items-center gap-1 text-[10px] text-red-500"
          title={run.errorMessage ?? undefined}
        >
          <AlertCircle size={10} /> Failed
        </span>
      );
    default:
      return null;
  }
}

const LOCATION_FILTERS = [
  { value: null, label: "All" },
  { value: "match", label: "Match" },
  { value: "uncertain", label: "Uncertain" },
  { value: "no_match", label: "No match" },
] as const;

function LocationFilterBar({
  filter,
  onFilterChange,
}: {
  filter: string | null;
  onFilterChange: (value: string | null) => void;
}) {
  return (
    <div className="mb-2 flex items-center gap-1">
      <MapPin size={12} className="mr-1 text-text-faint" />
      {LOCATION_FILTERS.map((f) => (
        <button
          key={f.label}
          type="button"
          onClick={() => onFilterChange(f.value)}
          className={`rounded px-2 py-0.5 text-[10px] font-medium transition-colors ${
            filter === f.value
              ? "bg-accent text-white"
              : "bg-transparent text-text-muted hover:bg-accent/10"
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

const MATCH_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  match: {
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-400",
    label: "Match",
  },
  uncertain: {
    bg: "bg-amber-100 dark:bg-amber-900/30",
    text: "text-amber-700 dark:text-amber-400",
    label: "Uncertain",
  },
  no_match: {
    bg: "bg-gray-100 dark:bg-gray-800",
    text: "text-gray-500 dark:text-gray-400",
    label: "No match",
  },
};

function LocationBadge({
  locationMatch,
  locationMatchSource,
  profileId,
  onOverride,
}: {
  locationMatch: string | null;
  locationMatchSource: string | null;
  profileId: string;
  onOverride?: (profileId: string, locationMatch: string) => void;
}) {
  if (!locationMatch) return null;

  const style = MATCH_STYLES[locationMatch];
  if (!style) return null;

  return (
    <span className="flex items-center gap-1">
      {onOverride ? (
        <select
          value={locationMatch}
          onChange={(e) => onOverride(profileId, e.target.value)}
          className={`cursor-pointer appearance-none rounded px-1.5 py-0.5 text-[10px] font-medium ${style.bg} ${style.text} border-none outline-none`}
          title="Change location classification"
        >
          <option value="match">Match</option>
          <option value="uncertain">Uncertain</option>
          <option value="no_match">No match</option>
        </select>
      ) : (
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${style.bg} ${style.text}`}>
          {style.label}
        </span>
      )}
      {locationMatchSource === "manual" && (
        <span className="text-[9px] text-text-faint">(manual)</span>
      )}
    </span>
  );
}
